/**
 * 予約中のお気に入りを「予約した図書館」ごとにまとめる。
 *
 * 図書館ごとに借りられる冊数の上限があるため、どの館で何冊予約しているかを
 * 一覧の見出しで把握できるようにする。
 * 確保メールで受取可になった本がある館は、受け取り忘れを防ぐため先頭に出す。
 */

import type { CalilBook } from '@/api/types/calil'
import type { LibraryFavorite, PickupState } from '@/api/types/library-favorite'

import { favoriteToBook } from './favorites-to-book'

export type ReservedLibraryGroup = {
  /** Calil の library_id。館を指定していない予約は null */
  libraryId: number | null
  name: string
  books: CalilBook[]
  /** 受取可の冊数 */
  readyCount: number
  /** 受取可の本のうち最も早い受取期限 (YYYY-MM-DD) */
  pickupDeadline: string | null
  /** 受取可の本の受取館 (例: 南部図書館) */
  pickupBranches: string[]
}

const UNSPECIFIED_KEY = 'unspecified'

export const UNSPECIFIED_LIBRARY_LABEL = '図書館を指定していない'

export const groupFavoritesByLibrary = (
  favorites: readonly LibraryFavorite[],
  mapping: Record<string, string>,
): ReservedLibraryGroup[] => {
  const groups = new Map<string, ReservedLibraryGroup>()

  for (const favorite of [...favorites].sort(comparePickup)) {
    const libraryId = favorite.reserved_library_id
    const key = libraryId == null ? UNSPECIFIED_KEY : String(libraryId)
    const book = favoriteToBook(favorite)
    const group = groups.get(key)
    if (group) {
      group.books.push(book)
      addPickup(group, favorite)
      continue
    }
    // 予約時点のスナップショットを優先し、無ければ Calil の mapping から引く。
    const name =
      libraryId == null
        ? UNSPECIFIED_LIBRARY_LABEL
        : (favorite.reserved_library_name ?? mapping[key] ?? `library:${libraryId}`)
    const created: ReservedLibraryGroup = {
      libraryId,
      name,
      books: [book],
      readyCount: 0,
      pickupDeadline: null,
      pickupBranches: [],
    }
    addPickup(created, favorite)
    groups.set(key, created)
  }

  return [...groups.values()].sort(compareGroups)
}

const addPickup = (group: ReservedLibraryGroup, favorite: LibraryFavorite) => {
  if (favorite.pickup_state !== 'ready') return
  group.readyCount += 1
  const deadline = favorite.pickup_deadline
  if (deadline && (group.pickupDeadline == null || deadline < group.pickupDeadline)) group.pickupDeadline = deadline
  const branch = favorite.pickup_branch
  if (branch && !group.pickupBranches.includes(branch)) group.pickupBranches.push(branch)
}

/** 館の中では 受取可 → 期限切れ → 受取済み → 確保待ち の順に並べる (同順位は元の並びを保つ) */
const PICKUP_ORDER = new Map<PickupState | null, number>([
  ['ready', 0],
  ['expired', 1],
  ['picked_up', 2],
  [null, 3],
])

const comparePickup = (a: LibraryFavorite, b: LibraryFavorite): number =>
  (PICKUP_ORDER.get(a.pickup_state) ?? 3) - (PICKUP_ORDER.get(b.pickup_state) ?? 3)

/** 受取可の本がある館を期限の早い順に先頭へ。それ以外は冊数の多い館を上に出す。館未指定は末尾に固定する。 */
const compareGroups = (a: ReservedLibraryGroup, b: ReservedLibraryGroup): number => {
  const aReady = a.readyCount > 0
  const bReady = b.readyCount > 0
  if (aReady !== bReady) return aReady ? -1 : 1
  if (aReady && a.pickupDeadline !== b.pickupDeadline) {
    return (a.pickupDeadline ?? '9999').localeCompare(b.pickupDeadline ?? '9999')
  }
  const aUnspecified = a.libraryId == null
  const bUnspecified = b.libraryId == null
  if (aUnspecified !== bUnspecified) return aUnspecified ? 1 : -1
  if (a.books.length !== b.books.length) return b.books.length - a.books.length
  return a.name.localeCompare(b.name, 'ja')
}
